/**
 * AdminImmeubleSubPage — Sous-pages des tuiles du Hub Immeubles.
 *
 * Route : /admin/immeubles/:masterId/:tileId
 *
 * Affiche le contenu d'une tuile en se basant sur :tileId :
 *   - portail / demandes / verifications / finance / comptabilite
 *     → re-utilise les composants Zone1/2/3 existants
 *   - baux-en-attente / archives
 *     → nouveaux composants dédiés
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Building2, Loader2, ArrowLeft, AlertCircle, Receipt, Banknote,
  Home, FileText, FileSpreadsheet, TrendingUp, TrendingDown, Wallet, Trash2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { toast } from "@/lib/toast";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import AdminImmeublePortalMirror from "@/pages/admin/immeubles/AdminImmeublePortalMirror";
import {
  Zone2Conformite, Zone3Comptabilite,
} from "./immeuble/AdminImmeubleZones";
import BauxEnAttenteTile from "./immeuble/tiles/BauxEnAttenteTile";
import SuiviSignaturesTile from "./immeuble/tiles/SuiviSignaturesTile";
import SuiviTravauxTile from "./immeuble/tiles/SuiviTravauxTile";
import ArchivesTile from "./immeuble/tiles/ArchivesTile";
import {
  AdminImmeubleExpenseModal, AdminImmeubleManualRevenueModal,
} from "./immeuble/AdminImmeubleModals";
import { API, authH } from "@/api";
import { fmtCAD } from "@/lib/format";


const TILE_TITLES = {
  portail: { label: "Vue portail", color: "blue" },
  demandes: { label: "Demandes en attente", color: "violet" },
  "suivi-travaux": { label: "Suivi des travaux", color: "amber" },
  finance: { label: "Données financières", color: "emerald" },
  comptabilite: { label: "Comptabilité", color: "orange" },
  "baux-en-attente": { label: "Bail en attente", color: "rose" },
  "suivi-signatures": { label: "Suivi signatures", color: "rose" },
  archives: { label: "Archives", color: "yellow" },
};

export default function AdminImmeubleSubPage() {
  const { masterId, tileId } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/masters/${masterId}/immeuble/overview`, { headers: authH() });
      setOverview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement dossier");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const tile = TILE_TITLES[tileId];
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!tile) return undefined;
    const name = overview?.master?.couple_display_name || "Dossier";
    setTrail([
      { label: "Immeubles", href: "/admin/immeubles" },
      { label: name, href: `/admin/immeubles?master_id=${masterId}` },
      { label: tile.label },
    ]);
    return () => setTrail(null);
  }, [tile, overview, masterId, setTrail]);

  if (!tile) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white border-2 border-rose-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="text-base font-bold text-slate-800">Tuile inconnue</h2>
          <p className="text-xs text-slate-500 mt-1">"{tileId}" n'est pas un module valide.</p>
          <button onClick={() => navigate(`/admin/immeubles?master_id=${masterId}`)}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour au hub
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!overview) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white border-2 border-rose-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="text-base font-bold text-slate-800">Dossier inaccessible</h2>
          <button onClick={() => navigate(`/admin/immeubles?master_id=${masterId}`)}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour au hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid={`immeuble-tile-${tileId}`}>
      {/* Header de la tuile */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate(`/admin/immeubles?master_id=${masterId}`)}
          className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition active:scale-90"
          aria-label="Retour au hub"
          data-testid="immeuble-tile-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Building2 className="w-4 h-4" />
          <span className="font-medium">{overview.master?.couple_display_name || "Dossier"}</span>
          <span className="text-slate-300">›</span>
          <span className="font-bold text-slate-800">{tile.label}</span>
        </div>
      </div>

      {/* Contenu */}
      {tileId === "portail" && <AdminImmeublePortalMirror masterId={masterId} />}
      {tileId === "demandes" && <Zone2Conformite masterId={masterId} overview={overview} />}
      {tileId === "suivi-travaux" && <SuiviTravauxTile masterId={masterId} />}
      {tileId === "finance" && <Zone3Comptabilite masterId={masterId} overview={overview} />}
      {tileId === "comptabilite" && (
        <ComptabiliteTile
          masterId={masterId}
          masterName={overview.master?.couple_display_name || ""}
          primaryClientId={overview.master?.primary_user_id || ""}
        />
      )}
      {tileId === "baux-en-attente" && <BauxEnAttenteTile masterId={masterId} />}
      {tileId === "suivi-signatures" && <SuiviSignaturesTile masterId={masterId} />}
      {tileId === "archives" && <ArchivesTile masterId={masterId} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sous-tuile Comptabilité — Saisies + Résumé + Graph + Exports + T777
// ════════════════════════════════════════════════════════════════════════════
const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

// Batch #14.105 — Mapping libellés méthodes paiement (UX)
const PAYMENT_METHOD_LABEL = {
  interac: "Interac e-Transfert",
  e_transfert: "e-Transfert",
  card: "Carte de crédit",
  credit_card: "Carte de crédit",
  cash: "Comptant",
  cheque: "Chèque",
  transfer: "Virement",
  other: "Autre",
  manual: "Manuel",
};

function ComptabiliteTile({ masterId, masterName, primaryClientId }) {
  const [showExpense, setShowExpense] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(0); // 0 = toute l'année, 1..12 = mois spécifique
  const [summary, setSummary] = useState(null);
  const [t777Total, setT777Total] = useState(0);
  const [monthly, setMonthly] = useState([]); // [{m, revenues, expenses}]
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [recentRevenues, setRecentRevenues] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const navigate = useNavigate();

  // Dates pour le filtre custom
  const period = useMemo(() => {
    const y = year;
    if (month === 0) {
      return { custom_start: `${y}-01-01`, custom_end: `${y}-12-31`, label: `Année ${y}` };
    }
    const mm = String(month).padStart(2, "0");
    const lastDay = new Date(y, month, 0).getDate();
    return { custom_start: `${y}-${mm}-01`, custom_end: `${y}-${mm}-${String(lastDay).padStart(2,"0")}`,
             label: `${MONTHS_FR[month-1]} ${y}` };
  }, [year, month]);

  const loadData = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const params = `period=custom&custom_start=${period.custom_start}&custom_end=${period.custom_end}`;
      const [pn, t7] = await Promise.all([
        axios.get(`${API}/admin/immeuble/${masterId}/zone4/profit-net?format=json&${params}`, { headers: authH() }),
        primaryClientId
          ? axios.get(`${API}/t777?client_id=${primaryClientId}&tax_year=${year}`, { headers: authH() })
              .catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setSummary(pn.data?.summary || null);

      // T777 — somme bureau à domicile pour l'année
      const t777Records = Array.isArray(t7.data) ? t7.data : [];
      const totalT777 = t777Records.reduce((s, r) => s + Number(r.deductible_amount || r.total_deduction || 0), 0);
      setT777Total(totalT777);

      // Construit la série mensuelle pour le graph (sur l'année entière, indépendant du filtre mois)
      const revs = pn.data?.revenues || [];
      const exps = pn.data?.expenses || [];
      const buckets = MONTHS_FR.map((_, i) => ({ m: MONTHS_FR[i].slice(0,3), revenues: 0, expenses: 0 }));
      const aggregate = (arr, key) => {
        for (const it of arr) {
          const dStr = (it.paid_at || "").slice(0, 10);
          if (!dStr) continue;
          const parts = dStr.split("-");
          if (parts.length !== 3) continue;
          const yr = parseInt(parts[0], 10);
          const mo = parseInt(parts[1], 10);
          if (yr !== year || mo < 1 || mo > 12) continue;
          buckets[mo - 1][key] += Number(it.amount || 0);
        }
      };
      // Pour le graph, on a besoin de toute l'année — refetch sans filtre mois si on a filtré sur un mois
      if (month !== 0) {
        const fullYear = await axios.get(
          `${API}/admin/immeuble/${masterId}/zone4/profit-net?format=json&period=custom&custom_start=${year}-01-01&custom_end=${year}-12-31`,
          { headers: authH() }
        );
        aggregate(fullYear.data?.revenues || [], "revenues");
        aggregate(fullYear.data?.expenses || [], "expenses");
      } else {
        aggregate(revs, "revenues");
        aggregate(exps, "expenses");
      }
      setMonthly(buckets);

      // Liste détaillée des revenus/dépenses récents (pour suppression depuis Comptabilité)
      setRecentRevenues((pn.data?.revenues || []).slice(0, 20));
      setRecentExpenses((pn.data?.expenses || []).slice(0, 20));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [masterId, year, month, period, primaryClientId]);

  useEffect(() => { loadData(); }, [loadData]);

  const downloadStatement = async (fmt) => {
    setDownloading(fmt);
    try {
      const params = `period=custom&custom_start=${period.custom_start}&custom_end=${period.custom_end}&format=${fmt}`;
      const r = await axios.get(
        `${API}/admin/immeuble/${masterId}/zone4/profit-net?${params}`,
        { headers: authH(), responseType: "blob" }
      );
      const ext = fmt === "excel" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etat-financier-${masterName.replace(/\s/g, "_")}-${period.label.replace(/\s/g, "-")}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`État financier ${ext.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur téléchargement");
    } finally {
      setDownloading(null);
    }
  };

  const openT777 = () => {
    if (!primaryClientId) {
      toast.error("Aucun client principal associé à ce dossier");
      return;
    }
    navigate(`/admin/home-office-calculator?year=${year}`);
  };

  // Batch #14.105 — Suppression d'une saisie en cas d'erreur
  const deleteRevenue = async (rev) => {
    if (!window.confirm(`Supprimer ce revenu de ${fmtCAD(rev.amount)} ?\n\nCette action est définitive et ne peut être annulée.`)) return;
    try {
      await axios.delete(`${API}/admin/pms/manual-revenues/${rev.id}`, { headers: authH() });
      toast.success("Revenu supprimé");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    }
  };

  const deleteExpense = async (exp) => {
    if (!window.confirm(`Supprimer cette dépense de ${fmtCAD(exp.amount)} ?\n\nCette action est définitive et ne peut être annulée.`)) return;
    try {
      // Les dépenses peuvent venir de 2 collections : admin_expense (modale admin) ou portal (proprio)
      const path = exp.source_collection === "admin_expense"
        ? `${API}/admin/pms/expenses/${exp.id}`
        : `${API}/admin/immeuble/zone3/journal/expense/${exp.id}`;
      await axios.delete(path, { headers: authH() });
      toast.success("Dépense supprimée");
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    }
  };

  const totalNet = summary
    ? Number(summary.total_revenues) - Number(summary.total_expenses) - Number(t777Total)
    : 0;

  return (
    <div className="space-y-4" data-testid="comptabilite-tile">
      {/* ════ Filtre période ════════════════════════════════════════════════ */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Période</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-orange-300"
                data-testid="compta-filter-year">
          {[0,-1,-2,-3].map(d => { const y = new Date().getFullYear()+d; return <option key={y} value={y}>{y}</option>; })}
        </select>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-orange-300"
                data-testid="compta-filter-month">
          <option value={0}>Toute l'année</option>
          {MONTHS_FR.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <span className="text-xs text-slate-500 ml-auto">Dossier : <strong>{masterName || "—"}</strong></span>
      </div>

      {/* ════ 3 boutons d'action ═══════════════════════════════════════════ */}
      <div className="bg-white border-2 border-orange-200 rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center">
            <Banknote className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">Comptabilité</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Saisies, calcul T777 et exports d'état financier — {period.label}.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ActionButton
            color="amber"
            icon={Receipt}
            title="Saisir une dépense"
            desc="Réparation, taxes, entretien…"
            onClick={() => setShowExpense(true)}
            testId="comptabilite-new-expense-btn"
          />
          <ActionButton
            color="emerald"
            icon={Banknote}
            title="Conciliation manuelle"
            desc="Interac, comptant, chèque…"
            onClick={() => setShowRevenue(true)}
            testId="comptabilite-new-revenue-btn"
          />
          <ActionButton
            color="indigo"
            icon={Home}
            title="Bureau à domicile (T777)"
            desc="Calcul de la déduction"
            onClick={openT777}
            testId="comptabilite-new-t777-btn"
            disabled={!primaryClientId}
          />
        </div>
      </div>

      {/* ════ 4 KPI cards ══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Revenus" value={fmtCAD(summary?.total_revenues)} icon={TrendingUp} tone="emerald"
                 sub={summary ? `${summary.nb_revenues} entrée(s)` : "—"} testId="compta-kpi-revenues" />
        <KpiCard label="Dépenses" value={fmtCAD(summary?.total_expenses)} icon={TrendingDown} tone="rose"
                 sub={summary ? `${summary.nb_expenses} entrée(s)` : "—"} testId="compta-kpi-expenses" />
        <KpiCard label="Bureau à domicile" value={fmtCAD(t777Total)} icon={Home} tone="indigo"
                 sub={`T777 — ${year}`} testId="compta-kpi-t777" />
        <KpiCard label="Résultat net" value={fmtCAD(totalNet)} icon={Wallet}
                 tone={totalNet >= 0 ? "blue" : "amber"}
                 sub="Revenus − Dépenses − Bureau"
                 testId="compta-kpi-net" />
      </div>

      {/* ════ Mini graph mensuel ═══════════════════════════════════════════ */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800">Revenus vs Dépenses — {year}</h3>
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">par mois</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : (
          <div className="h-56" data-testid="compta-monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="m" tick={{ fontSize: 10, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip
                  formatter={(v) => fmtCAD(v)}
                  contentStyle={{ fontSize: 11, borderRadius: 6 }}
                />
                <Bar dataKey="revenues" fill="#10b981" radius={[4, 4, 0, 0]} name="Revenus" />
                <Bar dataKey="expenses" fill="#f43f5e" radius={[4, 4, 0, 0]} name="Dépenses">
                  {monthly.map((_, i) => <Cell key={i} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ════ Dernières saisies (revenus + dépenses) — suppression possible ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="compta-recent-entries">
        <RecentList
          title="Revenus saisis (manuels)"
          color="emerald"
          items={recentRevenues.filter((r) => r.source === "manual")}
          emptyLabel="Aucun revenu manuel saisi sur cette période"
          onDelete={deleteRevenue}
          renderLabel={(r) => `${PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method || "—"} · ${r.tenant_name || "—"}`}
          testIdPrefix="compta-rev"
        />
        <RecentList
          title="Dépenses saisies"
          color="rose"
          items={recentExpenses}
          emptyLabel="Aucune dépense saisie sur cette période"
          onDelete={deleteExpense}
          renderLabel={(e) => `${e.category || e.label || "—"} · ${e.vendor || e.supplier_name || "—"}`}
          testIdPrefix="compta-exp"
        />
      </div>

      {/* ════ Exports PDF / Excel ══════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-slate-800">État financier — {period.label}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Profit net (Revenus − Dépenses − Commission cabinet)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => downloadStatement("pdf")}
            disabled={downloading !== null || !summary}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-40"
            data-testid="compta-export-pdf"
          >
            {downloading === "pdf"
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <FileText className="w-3.5 h-3.5" />}
            Télécharger PDF
          </button>
          <button
            onClick={() => downloadStatement("excel")}
            disabled={downloading !== null || !summary}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-40"
            data-testid="compta-export-excel"
          >
            {downloading === "excel"
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <FileSpreadsheet className="w-3.5 h-3.5" />}
            Télécharger Excel
          </button>
        </div>
      </div>

      <AdminImmeubleExpenseModal
        open={showExpense}
        onClose={() => setShowExpense(false)}
        initialMasterId={masterId}
        lockedMaster
        onCreated={() => { toast.success("Dépense enregistrée"); setShowExpense(false); loadData(); }}
      />
      <AdminImmeubleManualRevenueModal
        open={showRevenue}
        onClose={() => setShowRevenue(false)}
        initialMasterId={masterId}
        lockedMaster
        onCreated={() => { toast.success("Conciliation enregistrée"); setShowRevenue(false); loadData(); }}
      />
    </div>
  );
}

function ActionButton({ color, icon: Icon, title, desc, onClick, testId, disabled = false }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300",
    emerald: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300",
    indigo: "border-indigo-200 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-300",
  };
  const iconBg = {
    amber: "bg-amber-500", emerald: "bg-emerald-500", indigo: "bg-indigo-500",
  };
  const textColor = {
    amber: "text-amber-900", emerald: "text-emerald-900", indigo: "text-indigo-900",
  };
  const subColor = {
    amber: "text-amber-700", emerald: "text-emerald-700", indigo: "text-indigo-700",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group flex items-start gap-3 p-4 rounded-xl border-2 transition text-left disabled:opacity-40 disabled:cursor-not-allowed ${tones[color]}`}
      data-testid={testId}
    >
      <div className={`w-11 h-11 rounded-lg ${iconBg[color]} text-white flex items-center justify-center group-hover:scale-110 transition`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${textColor[color]}`}>{title}</div>
        <div className={`text-[11px] mt-0.5 ${subColor[color]}`}>{desc}</div>
      </div>
    </button>
  );
}

function KpiCard({ label, value, icon: Icon, tone, sub, testId }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</span>
        <Icon className="w-4 h-4 opacity-50" />
      </div>
      <div className="text-lg font-bold font-mono">{value}</div>
      <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>
    </div>
  );
}

// Batch #14.105 — Liste compacte des dernières saisies avec bouton supprimer
function RecentList({ title, color, items, emptyLabel, onDelete, renderLabel, testIdPrefix }) {
  const tones = {
    emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800", row: "bg-emerald-50/40 hover:bg-emerald-50" },
    rose: { border: "border-rose-200", bg: "bg-rose-50", text: "text-rose-800", row: "bg-rose-50/40 hover:bg-rose-50" },
  };
  const t = tones[color] || tones.emerald;
  return (
    <div className={`bg-white border-2 ${t.border} rounded-xl p-4`} data-testid={`${testIdPrefix}-list`}>
      <h3 className={`text-xs font-bold uppercase tracking-wider ${t.text} mb-2`}>{title} ({items.length})</h3>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-2">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto">
          {items.map((it) => (
            <li key={it.id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded ${t.row} group text-xs`}
                data-testid={`${testIdPrefix}-item-${it.id}`}>
              <span className="text-[10px] text-slate-500 tabular-nums">{(it.paid_at || "").slice(0, 10)}</span>
              <span className="flex-1 truncate text-slate-700">{renderLabel(it)}</span>
              <span className="font-mono text-slate-800 tabular-nums">{fmtCAD(it.amount)}</span>
              <button
                onClick={() => onDelete(it)}
                className="opacity-0 group-hover:opacity-100 transition px-1.5 py-0.5 rounded text-rose-600 hover:bg-rose-100"
                data-testid={`${testIdPrefix}-delete-${it.id}`}
                title="Supprimer cette entrée"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

